import { type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { CardTitleIcon } from '@/components/CardTitleIcon';
import { HaiIcon } from '@/components/icons';
import { useLibraryDateFormat } from '@/features/assets/libraryDate';
import type { AdAccountSummary, BrandKit, Project, UsageSummary } from '@/types';

import { homeAccountStatus, type HomeAccountTone } from '../homeAccountStatus';
import { AdsPlatformIcon } from '@/features/ads/AdsPlatformIcon';

/**
 * HomeSideLists — 首頁右欄的兩張清單卡(R292.17;對齊 D3 `首頁.html` 右欄)
 * -----------------------------------------------------------------------------
 * ① **廣告帳戶**:名稱 + 狀態 + 最後同步時間。
 *    ⚠️ **R298 起狀態字吃 `sync_status` 不只 `status`**(owner 2026-09-02 回報):
 *    改版前是 `status === 'ACTIVE' ? '已連線' : '需重新授權'` 兩分支,而**在 Google 端
 *    未啟用的帳戶 `status` 仍然是 `ACTIVE`** → 首頁對它寫「已連線」,而同一份 payload
 *    在廣告串接頁畫得出「未啟用」。判準單點 = [homeAccountStatus](../homeAccountStatus.ts)
 *    (連結層問題優先 → 已知同步狀態 → 認不得的值顯原碼);**同一份資料,不多打 API**。
 *    ✅ **R292.20 起畫得出來了** —— BE 的回寫已於 2026-08-21 上線
 *    (`ads/google/sync_repository.py` 成功寫 `OK` + 刷 `last_sync_at`,失敗寫
 *    `FAILED`/`TOKEN_EXPIRED` 並保留上次成功時間;已在 BE **main** 上取證)。
 *    ⚠️ **R263 判準原封不動**:`lastSyncAt` 有值才畫,**拿不到就不畫**——
 *    不可以 `?? '尚未同步過'`,那正是 R263 那張回報單的病灶(把「不知道」
 *    翻譯成「從未同步」)。DISCOVERED / FAILED 探索期帳戶本來就沒有時間,
 *    那一行就不出現。
 * ② **品牌規範**:名稱 + 「套用於 N 個專案」+ 更新日。
 *    「套用於 N 個」由**本工作區的專案清單**反查 `brandKitId` 算出來
 *    (owner 2026-08-30:跨工作區先不管)。⚠️ `GET /projects` cap 50,
 *    專案超過 50 時這個數會**低估** —— 所以文案用「套用於 N 個專案」不寫「共」。
 *
 * 兩張卡的標題都帶 D3 `.rc-h .ic` 的**綠色 icon**(廣告帳戶 = `link` 鎖鏈 /
 * 品牌規範 = `shield` 盾牌打勾;owner 2026-08-30 指名要)—— 兩顆都已在
 * `components/icons.tsx`(D3 `icons.js` 同一份),**不要在這裡另畫 SVG**。
 *
 * ③ **本月用量**(R292.20 接上;BE `dashboard.usage`)—— **R292.23 起排在最上面**
 *    (owner 指定;對齊 D3 右欄順序 本月用量 → 廣告帳戶 → 品牌規範):
 *    - `mode='credits'`(個人 / 非合約 —— beta 現況全部是這種)→ 畫「剩餘點數」
 *      = `available`,**不畫百分比**。個人錢包沒有月配額,湊一個百分比出來是
 *      假的經營指標(這正是上一版整張卡不畫的理由)。
 *    - `mode='quota'`(合約工作區)→ 百分比 = `quotaUsed / quotaTotal`,
 *      剩餘天數由 `periodEnd` 算;`periodEnd` 為 null 就只畫百分比。
 *    - `usage` 為 null(錢包讀不到 / 舊 BE 缺欄)→ **整張卡不畫**。
 *    ⚠️ quota 模式在 `quotaTotal` 缺席或 ≤0 時**退回不畫百分比** —— 除以 0 會得到
 *      Infinity/NaN,而畫一個「NaN%」比不畫更糟。
 */
export function HomeSideLists({
  accounts,
  brandKits,
  projects,
  usage,
}: {
  accounts: AdAccountSummary[] | null;
  brandKits: BrandKit[];
  projects: Project[] | null;
  /** `null` = 錢包讀不到 / 舊 BE 缺欄 → 整張卡不畫。 */
  usage: UsageSummary | null;
}) {
  const { t } = useTranslation('home');
  // CIS 日期寫法:每個顯示日期都要有年(`8月 3, 2026`);全站單點 = 素材庫那支
  const { fmtD } = useLibraryDateFormat();

  const usedBy = (brandKitId: string): number =>
    (projects ?? []).filter((p) => p.brandKitId === brandKitId).length;

  return (
    <>
      {usage && (
        <section style={cardStyle} data-testid="home-usage">
          <div style={headStyle}>
            <CardTitleIcon name="star" />
            <h2 style={titleStyle}>{t('side.usage.title')}</h2>
          </div>
          {(() => {
            const pct = usagePercent(usage);
            if (pct === null) {
              // credits 模式(或 quota 但分母不可用)→ 只講剩餘點數,不編百分比
              return (
                <div style={usageBodyStyle} data-testid="home-usage-credits">
                  <span style={usageBigStyle}>{usage.available.toLocaleString()}</span>
                  <span style={usageUnitStyle}>{t('side.usage.creditsUnit')}</span>
                </div>
              );
            }
            const days = daysUntil(usage.periodEnd);
            return (
              <div data-testid="home-usage-quota">
                <div style={usageBodyStyle}>
                  <span style={usageBigStyle}>{pct}%</span>
                  <span style={usageUnitStyle}>
                    {t('side.usage.quotaUsed', { used: usage.quotaUsed, total: usage.quotaTotal })}
                  </span>
                </div>
                <div style={usageBarStyle}>
                  <span style={{ ...usageBarFillStyle, width: `${Math.min(100, pct)}%` }} />
                </div>
                {/* `periodEnd` 讀不到 → 不寫剩餘天數(不編) */}
                {days !== null && (
                  <div style={usageNoteStyle}>{t('side.usage.daysLeft', { count: days })}</div>
                )}
              </div>
            );
          })()}
        </section>
      )}

      {accounts !== null && accounts.length > 0 && (
        <section style={cardStyle} data-testid="home-ad-accounts">
          <div style={headStyle}>
            <CardTitleIcon name="link" />
            <h2 style={titleStyle}>{t('side.accounts.title')}</h2>
            <span style={countStyle}>{t('side.accounts.count', { n: accounts.length })}</span>
          </div>
          <ul style={listStyle}>
            {accounts.map((a) => (
              <li key={a.adAccountId} style={rowStyle}>
                {/* R299 — Google / Meta 混列,方塊字母是唯一分得出平台的線索 */}
                <AdsPlatformIcon platform={a.platform} size="sm" style={{ marginRight: 8 }} />
                <span style={rowMainStyle}>
                  <span style={rowNameStyle}>{a.descriptiveName || a.customerId}</span>
                  {/* 🔒 R263 判準:有值才畫。**不要**加 `?? '尚未同步過'`。 */}
                  {a.lastSyncAt && (
                    <span style={rowSubStyle} data-testid={`home-acct-sync-${a.adAccountId}`}>
                      {t('side.accounts.lastSync', { at: fmtD(a.lastSyncAt) })}
                    </span>
                  )}
                </span>
                {(() => {
                  const st = homeAccountStatus(a);
                  const label =
                    st.kind === 'raw'
                      ? st.raw
                      : st.kind === 'sync'
                        ? t(`side.accounts.sync${st.value}` as const)
                        : st.kind === 'reauth'
                          ? t('side.accounts.needsReauth')
                          : st.kind === 'platformDisabled'
                            ? t('side.accounts.platformDisabled')
                            : t('side.accounts.linked');
                  return (
                    <span
                      style={tagStyleByTone[st.tone]}
                      title={st.kind === 'raw' ? st.raw : undefined}
                      data-testid={`home-acct-status-${a.adAccountId}`}
                    >
                      {label}
                    </span>
                  );
                })()}
              </li>
            ))}
          </ul>
          <Link to="/ads/accounts" style={moreLinkStyle} data-testid="home-ad-accounts-more">
            {t('side.accounts.manage')}
            <HaiIcon name="pgNext" size={12} />
          </Link>
        </section>
      )}

      {brandKits.length > 0 && (
        <section style={cardStyle} data-testid="home-brand-kits">
          <div style={headStyle}>
            <CardTitleIcon name="shield" />
            <h2 style={titleStyle}>{t('side.brand.title')}</h2>
            <span style={countStyle}>{t('side.brand.count', { n: brandKits.length })}</span>
          </div>
          <ul style={listStyle}>
            {brandKits.map((k) => (
              <li key={k.brandKitId} style={brandRowStyle}>
                <span style={rowNameStyle}>{k.name}</span>
                <span style={brandMetaStyle}>
                  {t('side.brand.usedBy', { n: usedBy(k.brandKitId) })}
                </span>
              </li>
            ))}
          </ul>
          <Link to="/brand-kits" style={moreLinkStyle} data-testid="home-brand-kits-more">
            {t('side.brand.manage')}
            <HaiIcon name="pgNext" size={12} />
          </Link>
        </section>
      )}
    </>
  );
}

/**
 * quota 模式的百分比;**回 null = 不要畫百分比**。
 * ⚠️ `quotaTotal` 缺席或 ≤0 時一定要回 null —— 除以 0 得到 Infinity/NaN,
 * 畫出「NaN%」比不畫更糟(而那正是「湊百分比」這條紅線想擋的東西)。
 */
function usagePercent(u: UsageSummary): number | null {
  if (u.mode !== 'quota') return null;
  // ⚠️ 用「是不是有限數字」判,**不要**寫 `=== null`:mapper 保證給 null,但這支也會
  // 收到未經 mapper 的物件(測試 / 舊快取),而 `undefined === null` 是 false →
  // 守衛穿過去就得到 `undefined / undefined` = NaN,畫出「NaN%」。
  const total = u.quotaTotal;
  const used = u.quotaUsed;
  if (!Number.isFinite(total as number) || (total as number) <= 0) return null;
  if (!Number.isFinite(used as number)) return null;
  return Math.round(((used as number) / (total as number)) * 100);
}

/** `periodEnd` 到今天的天數;讀不到 / 已過期 → null(不寫剩餘天數)。 */
function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  if (!Number.isFinite(end)) return null;
  const d = Math.ceil((end - Date.now()) / 86_400_000);
  return d > 0 ? d : null;
}

const cardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '16px 18px',
  borderRadius: 'var(--r-lg)',
  border: '1px solid var(--hairline)',
  background: 'var(--bg-card)',
};
const headStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 };
/** D3 `.rc-h .ic` = 主色(teal)。`--concept-fg` 兩個主題都翻得動,不用 `--tide-600` 死值。 */
const titleStyle: CSSProperties = { margin: 0, fontSize: 13.5, fontWeight: 800, color: 'var(--text-strong)' };
const countStyle: CSSProperties = { marginLeft: 'auto', fontSize: 11.5, color: 'var(--ink-500)' };
const listStyle: CSSProperties = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' };
const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '9px 0',
  borderTop: '1px solid var(--hairline)',
};
const brandRowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: '9px 0',
  borderTop: '1px solid var(--hairline)',
};
const rowMainStyle: CSSProperties = { flex: 1, minWidth: 0 };
const rowNameStyle: CSSProperties = {
  fontSize: 12.5,
  fontWeight: 600,
  color: 'var(--ink-200)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
};
const brandMetaStyle: CSSProperties = { fontSize: 11, color: 'var(--ink-500)' };
const rowSubStyle: CSSProperties = { display: 'block', marginTop: 2, fontSize: 11, color: 'var(--ink-500)' };
const usageBodyStyle: CSSProperties = { display: 'flex', alignItems: 'baseline', gap: 6, paddingTop: 2 };
const usageBigStyle: CSSProperties = {
  fontSize: 26,
  fontWeight: 800,
  lineHeight: 1,
  color: 'var(--text-strong)',
  // CIS:數字走 sans + tabular-nums(mono 只給 ID / 帳戶號 / 網域 / 代碼)
  fontVariantNumeric: 'tabular-nums',
};
const usageUnitStyle: CSSProperties = { fontSize: 11.5, color: 'var(--ink-500)' };
const usageBarStyle: CSSProperties = {
  marginTop: 10,
  height: 6,
  borderRadius: 3,
  background: 'var(--ink-800)',
  overflow: 'hidden',
};
const usageBarFillStyle: CSSProperties = {
  display: 'block',
  height: '100%',
  background: 'var(--tide-600)',
};
const usageNoteStyle: CSSProperties = { marginTop: 8, fontSize: 11, color: 'var(--ink-500)' };
const tagOkStyle: CSSProperties = { fontSize: 11, color: 'var(--ink-500)', whiteSpace: 'nowrap' };
const tagWarnStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--amber-700)',
  whiteSpace: 'nowrap',
};
const tagBadStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--rose-700)',
  whiteSpace: 'nowrap',
};
/**
 * 色調 → 樣式。`Record<HomeAccountTone, …>` 讓 tsc 逼窮舉(新增色調不會靜默落到
 * 某個 fallback)。前景一律 700 階 token —— R266 紅線:`--warn` 族是色相識別不是
 * 可讀前景。
 */
const tagStyleByTone: Record<HomeAccountTone, CSSProperties> = {
  ok: tagOkStyle,
  warn: tagWarnStyle,
  bad: tagBadStyle,
};
/** D3 `.rc-foot a`:12 / 700 / teal-700,文字 + pgNext 圖示(不用 unicode ›)。 */
const moreLinkStyle: CSSProperties = {
  marginTop: 2,
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--concept-fg)',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
};
